import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import usePageTitle from 'hooks/usePageTitle';
import { guid } from '@solimat/solimat-web-endpoint/lib/appointment/dto';
import PageMenu from 'components/blocks/PageMenu/PageMenu';
import Icon from 'components/blocks/Icon/Icon';
import MapGoogle2 from 'components/blocks/MapGoogle/MapGoogle2';
import IconCalendario from 'img/icons/svg/cita/calendario-azul.svg?react';
import IconHospital from 'img/icons/svg/cita/hospital-azul.svg?react';
import IconEspecialidad from 'img/icons/svg/cita/especialidad-azul.svg?react';
import IconEstado from 'img/icons/svg/cita/estado-azul.svg?react';
import { AppointmentVideoCall } from 'components/pages/AppointmentVideoCall/AppointmentVideoCall';
import Modal from 'components/blocks/Modal/Modal';
import LoaderMin from 'components/blocks/Loader/LoaderMin';
import HealthcareDocumentsGrid from 'components/blocks/Grids/HealthcareDocumentsGrid';
import FormValidateOTP from 'components/blocks/Forms/FormValidateOTP';
import NoData from 'components/pages/noData/NoData';
import { TextMessagesTemplates } from 'components/blocks/TextMessagesTemplates/TextMessagesTemplates';
import useGetAppointmentById from 'services/appointments/useGetAppointmentById';
import useGetDocumentsByAppointmentId from 'services/appointments/useGetDocumentsByAppointmentId';
import useGetRequestDocumentOtp from 'services/record/useGetRequestDocumentOtp';
import useModals2 from 'hooks/useModal2';
import './AppointmentDetailNew.scss';

const AppointmentDetailNew: React.FC = () => {
  usePageTitle('Detalle de Cita');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const appointmentId = id as guid;

  const [appointment, setAppointment] = useState<any>(null);
  const [documentID, setDocumentID] = useState<string | guid>('');

  const {
    getAppointmentById,
    loadingGetAppointmentById,
    errorGetAppointmentById,
    dataAppointment,
  } = useGetAppointmentById();

  const {
    documentsByAppointmentId,
    getDocumentstByAppointmentId,
  } = useGetDocumentsByAppointmentId();

  const { getRequestDocumentOtp, errorGetRequestDocumentOtp } = useGetRequestDocumentOtp();

  const {
    collapsed2,
    handleOpenModal2,
    handleCloseModal2,
    handleDisableCloseButton2,
    disabledCloseButton2,
  } = useModals2();

  useEffect(() => {
    getAppointmentById({ Id: appointmentId }, (data: any) => {
      setAppointment(data);
      if (data?.userId && data?.prestacionId && data?.citaId) {
        getDocumentstByAppointmentId(data.userId, data.prestacionId, data.citaId);
      }
    });
  }, []);

  const handleRequestDocument = (docId: string) => {
    setDocumentID(docId);
    handleOpenModal2();
    getRequestDocumentOtp(appointment?.userId, appointment?.prestacionId, docId);
  };

  const redirect = (lat: number, lng: number) => {
    window.open(`https://maps.google.com/?q=${lat},${lng}`);
  };

  if (errorGetAppointmentById) {
    return <section className="section"><div className="container">{TextMessagesTemplates.errorResponse}</div></section>;
  }

  if (loadingGetAppointmentById || !dataAppointment) {
    return <section className="section"><div className="container">{TextMessagesTemplates.loading}</div></section>;
  }

  const a = appointment || {};
  const stateKey = a.realizada ? 'success' : a.reprogramada ? 'warn' : 'info';
  const stateLabel = a.realizada ? 'Realizada' : a.reprogramada ? 'Reprogramada' : (a.estado || 'Confirmada');

  const address = a.centro
    ? `${a.centro.tipoCalle} ${a.centro.nombreCalle}, ${a.centro.numeroCalle} — ${a.centro.localidad}, ${a.centro.provincia} ${a.centro.codigoPostal}`
    : '';

  return (
    <>
      <PageMenu
        title={a.especialidad || 'Cita'}
        subtitle={a.centro?.descripcion || ''}
        breadcrumbs={['Mis citas', a.especialidad || 'Cita']}
      >
        <div className="btns-header-container">
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/mis-citas')}>
            <Icon name="arrow-left" size={16} /> Mis citas
          </button>
        </div>
      </PageMenu>

      <section className="section apptd-section">
        <div className="container">

          <h3 className="section-h"><span className="section-h__bar" />Información de la cita</h3>

          <div className="apptd-card">
            <div className="apptd-card__head">
              <span className="apptd-card__patient">{(a.especialidad || 'CITA').toUpperCase()}</span>
              <span className={`pill pill--dot pill--${stateKey}`}>{stateLabel}</span>
            </div>
            <div className="apptd-card__grid">
              <div className="apptd-item">
                <span className="apptd-item__ic apptd-item__ic--svg">
                  <IconCalendario width={24} height={24} />
                </span>
                <div>
                  <div className="apptd-item__k">Fecha y hora</div>
                  <div className="apptd-item__v">{a.formated_start} · {a.formated_hour} h</div>
                </div>
              </div>
              <div className="apptd-item">
                <span className="apptd-item__ic apptd-item__ic--svg">
                  <IconHospital width={24} height={24} />
                </span>
                <div>
                  <div className="apptd-item__k">Centro</div>
                  <div className="apptd-item__v">{a.centro?.descripcion}</div>
                </div>
              </div>
              <div className="apptd-item">
                <span className="apptd-item__ic apptd-item__ic--svg">
                  <IconEspecialidad width={24} height={24} />
                </span>
                <div>
                  <div className="apptd-item__k">Especialidad</div>
                  <div className="apptd-item__v">{a.especialidad}</div>
                </div>
              </div>
              <div className="apptd-item">
                <span className="apptd-item__ic apptd-item__ic--svg">
                  <IconEstado width={24} height={24} />
                </span>
                <div>
                  <div className="apptd-item__k">Estado de la cita</div>
                  <div className={`apptd-item__v apptd-item__v--${stateKey}`}>{stateLabel}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12 map-appointment-container">
            {a.linkVideoConsulta ? (
              <AppointmentVideoCall appointment={a} />
            ) : (
              <MapGoogle2
                latitudeCenter={a.centro.latitud}
                longitudeCenter={a.centro.longitud}
                redirect={redirect}
                zoom={18}
                title={a.centro.descripcion}
              />
            )}
          </div>

          <h3 className="section-h"><span className="section-h__bar" />Otros datos de interés</h3>

          <div className="apptd-misc">
            <dl>
              {!a.linkVideoConsulta && address && (
                <div><dt>Dirección del centro</dt><dd>{address}</dd></div>
              )}
              {a.centro?.telefono && (
                <div>
                  <dt>Teléfono del centro</dt>
                  <dd><a href={`tel:${a.centro.telefono.replace(/\s/g, '')}`}>{a.centro.telefono}</a></dd>
                </div>
              )}
              {a.doctor?.nombre && (
                <div><dt>Asistencial</dt><dd>{a.doctor.nombre}</dd></div>
              )}
              <div><dt>Tipo de prestación</dt><dd>{a.tipoActividad || '—'}</dd></div>
              <div><dt>Reprogramada</dt><dd>{a.reprogramada ? 'Sí' : 'No'}</dd></div>
              {a.especialidad !== 'REHABILITACION' && (
                <div><dt>Observaciones</dt><dd>{a.mensaje || 'Sin observaciones'}</dd></div>
              )}
            </dl>
          </div>

          <h3 className="section-h"><span className="section-h__bar" />Documentos de cita</h3>

          {!documentsByAppointmentId || documentsByAppointmentId.length === 0 ? (
            <NoData message="No hay documentación disponible" />
          ) : (
            <HealthcareDocumentsGrid
              handleRequestDocument={handleRequestDocument}
              documents={documentsByAppointmentId.map((doc: any) => ({
                nombre: doc.nombre,
                extension: doc.extension,
                company: doc.company ?? '—',
                fecha: doc.fecha,
                id: doc.id,
                requiereOtp: doc.requiereOtp,
                uri: doc.uri,
              }))}
            />
          )}

          <Modal
            disabledCloseButton={disabledCloseButton2}
            collapsed={collapsed2}
            onClose={handleCloseModal2}
            title="Introduzca el código"
            subtitle="Para visualizar el documento, introduzca el código que hemos enviado a su teléfono"
          >
            {collapsed2 ? (
              <div className="ghost-form" />
            ) : (
              <>
                {documentsByAppointmentId ? (
                  <FormValidateOTP
                    handleDisableCloseButton={handleDisableCloseButton2}
                    onClose={handleCloseModal2}
                    documentID={documentID}
                    userId={appointment?.userId}
                    prestacionId={appointment?.prestacionId}
                  />
                ) : (
                  <>
                    {errorGetRequestDocumentOtp ? (
                      <div>
                        <h3 className="modal_title">Error</h3>
                        <p className="c-center">Lo sentimos, se ha producido un error</p>
                      </div>
                    ) : (
                      <LoaderMin />
                    )}
                  </>
                )}
              </>
            )}
          </Modal>

        </div>
      </section>
    </>
  );
};

export default AppointmentDetailNew;
